import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Package, Loader, AlertCircle } from 'lucide-react';
import './PaymentResultPage.css';


const PaymentSuccessPage: React.FC = () => {
    const navigate = useNavigate();
    useSearchParams();
    const [checking, setChecking] = useState(true);
    const [accessGranted, setAccessGranted] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const intervalRef = React.useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // ✅ Limpar flag de compra pendente
        localStorage.removeItem('pendingPurchase');

        // ✅ POLLING: Verificar se o webhook já liberou o acesso
        const checkAccess = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await fetch('/api/payments/my-purchases', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.purchases && data.purchases.length > 0) {
                        console.log('✅ Acesso liberado! Redirecionando...');
                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                        }
                        setAccessGranted(true);
                        navigate('/dashboard');
                        return true;
                    }
                }
                return false;
            } catch (error) {
                console.error('Erro ao verificar acesso:', error);
                return false;
            }
        };

        // Verificar imediatamente
        checkAccess();

        // ✅ Verificar a cada 3 segundos
        intervalRef.current = setInterval(async () => {
            const granted = await checkAccess();
            setAttempts(prev => prev + 1);

            if (granted) {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                }
                setAccessGranted(true);
                setChecking(false);

                // ✅ Redirecionar após 2 segundos
                setTimeout(() => {
                    navigate('/dashboard');
                }, 2000);
            }
        }, 3000);

        // ✅ Parar após 30 segundos (10 tentativas)
        const timeout = setTimeout(() => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            setChecking(false);
            // ✅ NÃO forçar accessGranted
        }, 30000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            clearTimeout(timeout);
        };
    }, [navigate]);

    return (
        <div className="payment-result-page success">
            <div className="result-container">
                <div className="result-icon success-icon">
                    <CheckCircle size={80} strokeWidth={1.5} />
                </div>

                <h1>Pagamento Aprovado!</h1>

                {checking ? (
                    <>
                        <p className="result-message">
                            Processando seu acesso...
                        </p>
                        <div className="checking-access">
                            <Loader size={24} className="spinner-icon" />
                            <span>Verificando liberação... ({attempts}/10)</span>
                        </div>
                        <p className="result-hint">
                            Aguarde enquanto confirmamos seu pagamento
                        </p>
                    </>
                ) : accessGranted ? (
                    <>
                        <p className="result-message">
                            Sua compra foi processada com sucesso. Seu acesso foi liberado!
                        </p>

                        <div className="result-actions">
                            <button
                                className="btn-primary"
                                onClick={() => navigate('/dashboard')}
                            >
                                <Package size={20} />
                                Ir para Dashboard
                                <ArrowRight size={20} />
                            </button>

                            <button
                                className="btn-secondary"
                                onClick={() => navigate('/marketplace')}
                            >
                                Continuar Comprando
                            </button>
                        </div>

                        <div className="success-info">
                            <div className="info-box">
                                <h3>O que acontece agora?</h3>
                                <ul>
                                    <li>✅ Seu acesso foi liberado automaticamente</li>
                                    <li>✅ Você pode começar a estudar imediatamente</li>
                                    <li>✅ O recibo foi enviado para seu email</li>
                                </ul>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="pending-confirmation">
                            <AlertCircle size={48} />
                            <h3>Confirmando pagamento...</h3>
                            <p>
                                Ainda estamos processando seu pagamento.
                                Seu acesso será liberado automaticamente em instantes.
                            </p>
                            <p className="pending-hint">
                                Você pode fechar esta página. Quando o pagamento for confirmado,
                                seu conteúdo estará disponível no dashboard.
                            </p>
                        </div>

                        <div className="result-actions">
                            <button
                                className="btn-primary"
                                onClick={() => navigate('/dashboard')}
                            >
                                Ir para Dashboard
                            </button>
                            <button
                                className="btn-secondary"
                                onClick={() => window.location.reload()}
                            >
                                Tentar Novamente
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentSuccessPage;